import type React from "react";
import { Link } from "@tanstack/react-router";
import { STATES_DATA, slugify } from "@/data/states";

const MAJOR_METROS: { city: string; stateSlug: string; stateCode: string }[] = [
  { city: "New York City", stateSlug: "new-york", stateCode: "NY" },
  { city: "Los Angeles", stateSlug: "california", stateCode: "CA" },
  { city: "Chicago", stateSlug: "illinois", stateCode: "IL" },
  { city: "Houston", stateSlug: "texas", stateCode: "TX" },
  { city: "Phoenix", stateSlug: "arizona", stateCode: "AZ" },
  { city: "Philadelphia", stateSlug: "pennsylvania", stateCode: "PA" },
  { city: "San Antonio", stateSlug: "texas", stateCode: "TX" },
  { city: "San Diego", stateSlug: "california", stateCode: "CA" },
  { city: "Dallas", stateSlug: "texas", stateCode: "TX" },
  { city: "Austin", stateSlug: "texas", stateCode: "TX" },
  { city: "Seattle", stateSlug: "washington", stateCode: "WA" },
  { city: "Denver", stateSlug: "colorado", stateCode: "CO" },
  { city: "Boston", stateSlug: "massachusetts", stateCode: "MA" },
  { city: "Atlanta", stateSlug: "georgia", stateCode: "GA" },
  { city: "Miami", stateSlug: "florida", stateCode: "FL" },
  { city: "Detroit", stateSlug: "michigan", stateCode: "MI" },
  { city: "San Francisco", stateSlug: "california", stateCode: "CA" },
];

const COLS = 13;
const ROWS = 7;
const CELL = 64;
const GAP = 4;
const WIDTH = COLS * (CELL + GAP);
const HEIGHT = ROWS * (CELL + GAP);

// Positions for up to 4 city pins inside each state tile
const PIN_OFFSETS: { dx: number; dy: number }[] = [
  { dx: 14, dy: 46 },
  { dx: 34, dy: 50 },
  { dx: 50, dy: 40 },
  { dx: 22, dy: 32 },
];

export function USAMap({ compact = false }: { compact?: boolean }) {
  const size = compact ? 0.7 : 1;
  return (
    <div className="relative w-full overflow-x-auto">
      <svg
        viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
        className="w-full h-auto mx-auto"
        style={{ maxWidth: `${WIDTH * size}px` }}
        role="img"
        aria-label="USA Nationwide Vending Machine Coverage Map — all 50 states with major city pins"
      >
        <defs>
          <linearGradient id="stateGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#3A6FA0" />
            <stop offset="100%" stopColor="#2F5D8A" />
          </linearGradient>
          <filter id="pinShadow" x="-50%" y="-50%" width="200%" height="200%">
            <feDropShadow dx="0" dy="1" stdDeviation="1" floodOpacity="0.5" />
          </filter>
        </defs>

        {/* State tiles (clickable → state page) */}
        {STATES_DATA.map((s) => {
          const x = s.col * (CELL + GAP);
          const y = s.row * (CELL + GAP);
          const stateLabel = `${s.name} — view free vending machine coverage in ${s.name}`;
          return (
            <g key={`state-${s.code}`} className="group">
              <Link
                to="/states/$state"
                params={{ state: s.slug }}
                aria-label={stateLabel}
                role="link"
                tabIndex={0}
                onKeyDown={(e: React.KeyboardEvent<HTMLAnchorElement>) => {
                  if (e.key === " " || e.key === "Spacebar") {
                    e.preventDefault();
                    (e.currentTarget as HTMLAnchorElement).click();
                  }
                }}
                className="focus:outline-none [&:focus-visible_rect]:stroke-[#F57C00] [&:focus-visible_rect]:stroke-[3px]"
              >
                <rect
                  x={x}
                  y={y}
                  width={CELL}
                  height={CELL}
                  rx={6}
                  fill="url(#stateGrad)"
                  stroke="#1F4670"
                  strokeWidth={2}
                  className="cursor-pointer transition-all hover:fill-[#3A6FA0]"
                />
                <text
                  x={x + CELL / 2}
                  y={y + 18}
                  textAnchor="middle"
                  dominantBaseline="middle"
                  fill="#fff"
                  fontFamily="Oswald, sans-serif"
                  fontWeight={700}
                  fontSize={18}
                  className="pointer-events-none select-none"
                >
                  {s.code}
                </text>
                <title>{stateLabel}</title>
              </Link>
            </g>
          );
        })}

        {/* City pins (clickable → city/town page, hover/focus shows city name) */}
        {STATES_DATA.map((s) => {
          const x = s.col * (CELL + GAP);
          const y = s.row * (CELL + GAP);
          return s.cities.slice(0, 4).map((city, i) => {
            const off = PIN_OFFSETS[i];
            const cx = x + off.dx;
            const cy = y + off.dy;
            const pinLabel = `${city}, ${s.code} — view free vending machine coverage`;
            return (
              <g key={`pin-${s.code}-${city}`} className="cursor-pointer">
                <Link
                  to="/states/$state/$town"
                  params={{ state: s.slug, town: slugify(city) }}
                  aria-label={pinLabel}
                  role="link"
                  tabIndex={0}
                  onKeyDown={(e: React.KeyboardEvent<HTMLAnchorElement>) => {
                    if (e.key === " " || e.key === "Spacebar") {
                      e.preventDefault();
                      (e.currentTarget as HTMLAnchorElement).click();
                    }
                  }}
                  className="focus:outline-none [&:focus-visible_circle:last-of-type]:stroke-[#F57C00] [&:focus-visible_circle:last-of-type]:stroke-[2.5px]"
                >
                  {/* hit-target */}
                  <circle cx={cx} cy={cy} r={8} fill="transparent" />
                  <circle
                    cx={cx}
                    cy={cy}
                    r={3.5}
                    fill="#F57C00"
                    stroke="#fff"
                    strokeWidth={1.2}
                    filter="url(#pinShadow)"
                    className="transition-all hover:r-5"
                  />
                  <title>{pinLabel}</title>
                </Link>
              </g>
            );
          });
        })}
      </svg>

      <div className="mt-4 flex flex-wrap items-center justify-center gap-4 text-xs text-muted-foreground">
        <span className="inline-flex items-center gap-1.5">
          <span className="h-3 w-3 rounded-sm bg-steel" /> State tile (click for state)
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="h-3 w-3 rounded-full bg-safety border border-white" /> City pin (hover &
          click)
        </span>
      </div>

      {!compact && (
        <div className="mt-10">
          <h3 className="font-display text-xl uppercase text-center tracking-wide">
            Jump Directly to a Major Metro
          </h3>
          <p className="mt-2 text-center text-sm text-muted-foreground">
            Skip the state page — click any metro to view local coverage.
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-2">
            {MAJOR_METROS.map((m) => (
              <Link
                key={`${m.city}-${m.stateCode}`}
                to="/states/$state/$town"
                params={{ state: m.stateSlug, town: slugify(m.city) }}
                title={`Free vending machines in ${m.city}, ${m.stateCode}`}
                className="rounded-full border border-border bg-card px-4 py-2 text-sm font-medium hover:border-safety hover:bg-safety hover:text-safety-foreground transition"
              >
                {m.city} <span className="text-xs opacity-70">{m.stateCode}</span>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
